// Yolutap — display-side scoring helpers (max per archetype + labels)
//
// The REPORT uses a *display ceiling* that:
//   - is high enough that any realistic user's score fits
//   - matches the reference PDFs the team designed against
//   - lets the bar chart use a fixed % scale (so the label "X of Y" and the
//     bar width are always in sync)
//
// Source of truth:
//   10-11 → questions_10_11.json    `scoring_model.practical_max_score` = 30
//   12-13 → questions_12_13.json    `scoring_model.practical_max_score` = 24
//   14-15 → questions_14_15_v2.json `scoring_model.practical_max_score` = 16
//           (Plan C rebalance June 8, 2026 — uniform 16 slots per
//            archetype × 1 pt = 16 = practical_max_score, by construction)
//   16-18 / 18-22 → fallback constants (each questions_*.json still needs
//                   `practical_max_score` to be set explicitly).

import questions1011 from "@/data/questions_10_11.json";
import questions1213 from "@/data/questions_12_13.json";
import questions1415 from "@/data/questions_14_15_v2.json";
// 16-18 читает ЖИВОЙ банк v2 (16_18_v2_2026_07) — его загрузчик
// (src/lib/yolutap/loader.ts) выдаёт новым сессиям. До 2026-10 здесь стоял v1:
// раскладка слотов у них совпадает (по 16 на архетип), так что на баллы это
// не влияло, но таблица по умолчанию должна смотреть на тот банк, по которому
// считают. Сессии на v1 нормализуются по своему банку: calculateResult
// передаёт вопросы сессии в normalizeScoresPerArchetype.
// Методологический черновик src/data/questions_16_18.json (archetype_block +
// history_block) сюда не подключать — см. getSlotCountsByArchetype ниже.
import questions1618 from "@/data/questions_16_18_v2.json";
import type { ArchetypeCode, ScoresByArchetype } from "@/lib/yolutap/types";
import { ARCHETYPE_CODES } from "@/lib/yolutap/types";

export type AgeGroupId = "10_11" | "12_13" | "14_15" | "16_18" | "18_22";

type ScoringModel = {
  practical_max_score?: number;
  theoretical_max_score?: number;
  points_per_answer?: number;
  max_score_note?: string;
};

const SCORING_1011 = (questions1011 as unknown as { scoring_model?: ScoringModel })
  .scoring_model;
const SCORING_1213 = (questions1213 as unknown as { scoring_model?: ScoringModel })
  .scoring_model;
const SCORING_1415 = (questions1415 as unknown as { scoring_model?: ScoringModel })
  .scoring_model;
const SCORING_1618 = (questions1618 as unknown as { scoring_model?: ScoringModel })
  .scoring_model;

/**
 * Fallback constants for ages whose questions_*.json doesn't yet expose
 * `scoring_model.practical_max_score`. Keep in sync with the reference PDFs
 * and SYSTEM_REFERENCE v1.1 Часть 5:
 *   10-11 → 30   (also explicit in questions_10_11.json)
 *   12-13 → 24   (also explicit in questions_12_13.json)
 *   14-15 → 16   (also explicit in questions_14_15_v2.json — kept here
 *                 as defensive fallback if the file is ever missing the field;
 *                 was 19 before Plan C rebalance, June 8, 2026)
 *   16-18 → 16   (TODO: declare in questions_16_18.json)
 *   18-22 → 16   (TODO: declare in questions_18_22.json)
 */
const FALLBACK_MAX: Record<AgeGroupId, number> = {
  "10_11": 30,
  "12_13": 24,
  "14_15": 16,
  "16_18": 16,
  "18_22": 16,
};

export function getMaxScore(ageGroup: AgeGroupId | string): number {
  if (ageGroup === "10_11" && SCORING_1011?.practical_max_score) {
    return SCORING_1011.practical_max_score;
  }
  if (ageGroup === "12_13" && SCORING_1213?.practical_max_score) {
    return SCORING_1213.practical_max_score;
  }
  if (ageGroup === "14_15" && SCORING_1415?.practical_max_score) {
    return SCORING_1415.practical_max_score;
  }
  if (ageGroup === "16_18" && SCORING_1618?.practical_max_score) {
    return SCORING_1618.practical_max_score;
  }
  return FALLBACK_MAX[ageGroup as AgeGroupId] ?? 20;
}

/**
 * `points_per_answer` per band, read from the same scoring_model object as
 * `getMaxScore` — kept here so Plan-B normalization, audits and renderers
 * have one source of truth instead of duplicating the constant.
 */
export function getPointsPerAnswer(ageGroup: AgeGroupId | string): number {
  if (ageGroup === "10_11" && SCORING_1011?.points_per_answer) return SCORING_1011.points_per_answer;
  if (ageGroup === "12_13" && SCORING_1213?.points_per_answer) return SCORING_1213.points_per_answer;
  if (ageGroup === "14_15" && SCORING_1415?.points_per_answer) return SCORING_1415.points_per_answer;
  if (ageGroup === "16_18" && SCORING_1618?.points_per_answer) return SCORING_1618.points_per_answer;
  return 1;
}

/**
 * Slot counts per archetype, computed from the SAME bank the question
 * loader serves to the user.
 *
 * Returns the number of answer slots tagged with each archetype code in
 * the band's questionnaire. Used by Plan-B per-archetype normalization
 * (10-11 + 16-18) — the normalizer needs per-archetype `true_max` =
 * `slots × points_per_answer` so a score earned across N slots scales
 * to the same display cap as one earned across M slots.
 *
 * Critical correctness invariant: the bank we count from here MUST be
 * the bank that the user's raw scores accumulate from (the loader bank).
 * If a parallel methodologist-facing draft has a different slot
 * distribution, the normalizer would divide by the wrong true_max and
 * distort scaled values / rank.
 *
 * That regression hit 16-18 (caught June 17 2026): `questions_16_18.json`
 * (draft) had spread=4 while loader-served `questions_16_18_v1.json` had
 * spread=0; the normalizer was reading the draft's distribution,
 * producing scaled values that differed from raw even for partial scores
 * and flipping primary_archetype in mixed-leaning profiles. Fix: read
 * from the loader bank for every band, uniformly — no per-band schema
 * branches.
 *
 * Shape (all bands except 18-22): `child_test.questions[].answers[]` with
 *   - `archetype_code: ArchetypeCode`
 *   - `points: number`  (currently uniform per band: 10-11 all 2pt,
 *                        12-13 / 14-15 / 16-18 all 1pt)
 * Counts are the raw slot count per archetype. The normalizer multiplies
 * by ppA to derive max-raw; for the current banks (uniform per-bank
 * points) count × ppA = sum-of-points = max-raw. A future bank with
 * per-answer variable points would need BOTH a points-weighted slot
 * function AND a normalizer formula update (drop the × ppA) — flag at
 * that point, don't pre-emptively over-engineer here.
 *
 * 18-22 uses RIASEC scoring through reports1822.ts, not the archetype
 * pipeline → returns {}.
 */
type LoaderBank = {
  child_test?: {
    questions?: Array<{
      answers?: Array<{ archetype_code?: string }>;
    }>;
  };
};

const BANK_BY_AGE: Partial<Record<AgeGroupId, LoaderBank>> = {
  "10_11": questions1011 as unknown as LoaderBank,
  "12_13": questions1213 as unknown as LoaderBank,
  "14_15": questions1415 as unknown as LoaderBank,
  "16_18": questions1618 as unknown as LoaderBank,
  // 18-22 intentionally absent — uses the RIASEC pipeline, returns {}.
};

export function getSlotCountsByArchetype(
  ageGroup: AgeGroupId | string,
): Partial<Record<ArchetypeCode, number>> {
  const bank = BANK_BY_AGE[ageGroup as AgeGroupId];
  if (!bank) return {};
  return countSlots(bank.child_test?.questions ?? []);
}

function countSlots(
  qs: Array<{ answers?: Array<{ archetype_code?: string }> }>,
): Partial<Record<ArchetypeCode, number>> {
  const counts: Partial<Record<ArchetypeCode, number>> = {};
  for (const q of qs) {
    for (const ans of q.answers ?? []) {
      const code = ans.archetype_code as ArchetypeCode | undefined;
      if (code) counts[code] = (counts[code] ?? 0) + 1;
    }
  }
  return counts;
}

/**
 * Plan B per-archetype normalization.
 *
 *   scaled[arc] = round( raw[arc] / true_max[arc] × cap[band] )
 *   true_max[arc] = slots[arc] × points_per_answer[band]
 *
 * Returns a copy of `rawScores` with every defined archetype rescaled to
 * the band's displayed cap. Archetypes with no slots in the data are
 * passed through unchanged (defensive — shouldn't happen for the bands
 * Plan B is active on). Use the returned object as `child_scores` for
 * ranking, display, bar widths and mixed_profile diff.
 *
 * Currently active only for 10-11 (uniform slot distribution, so scaled
 * is just raw/2 — overflow goes away, ranking unchanged) and 16-18
 * (skewed distribution, so ranking can shift — this is the correction).
 *
 * Tiebreak when two archetypes round to the same scaled value is the
 * caller's responsibility — pass `child_scores_raw` alongside to break
 * ties by raw score.
 */
export function normalizeScoresPerArchetype(
  rawScores: ScoresByArchetype,
  ageGroup: AgeGroupId | string,
  // Вопросы, по которым посчитаны rawScores (банк версии сессии). Если
  // переданы — слоты считаются по ним, иначе по банку группы по умолчанию.
  questions?: Array<{ answers?: Array<{ archetype_code?: string }> }>,
): ScoresByArchetype {
  const slots = questions
    ? countSlots(questions)
    : getSlotCountsByArchetype(ageGroup);
  const ppA = getPointsPerAnswer(ageGroup);
  const cap = getMaxScore(ageGroup);
  const out = {} as ScoresByArchetype;
  for (const code of ARCHETYPE_CODES) {
    const raw = rawScores[code] ?? 0;
    const s = slots[code] ?? 0;
    const trueMax = s * ppA;
    if (trueMax <= 0) {
      out[code] = raw;
      continue;
    }
    const scaled = Math.round((raw / trueMax) * cap);
    // Belt-and-suspenders: clamp to [0, cap]. Per the math this never
    // fires (raw is bounded by trueMax by construction), but the
    // safety-net keeps a future content-drift regression from sneaking
    // a > cap value into the renderer's bar-width math.
    out[code] = Math.max(0, Math.min(cap, scaled));
  }
  return out;
}

/** Bands that go through Plan B per-archetype normalization. Other bands
 *  read their raw sum directly. */
export const PLAN_B_BANDS: Set<string> = new Set(["10_11", "16_18"]);

/** Kept exported for backwards compatibility (any consumers reading the const). */
export const MAX_SCORE_PER_ARCHETYPE: Record<AgeGroupId, number> = {
  "10_11": SCORING_1011?.practical_max_score ?? FALLBACK_MAX["10_11"],
  "12_13": SCORING_1213?.practical_max_score ?? FALLBACK_MAX["12_13"],
  "14_15": SCORING_1415?.practical_max_score ?? FALLBACK_MAX["14_15"],
  "16_18": FALLBACK_MAX["16_18"],
  "18_22": FALLBACK_MAX["18_22"],
};

/**
 * Подпись под баллом на страницах архетипа: «/ N» — читается как «7 / 16».
 * Одинакова во всех языках. Раньше было «16-dən» / «из 16 возм.» /
 * «of 16 possible»: азербайджанский аффикс зависит от числа (16-dan,
 * 20-dən, 30-dan), и для 16 он стоял неверный (21.10.2026). `lang` оставлен
 * в подписи, чтобы не трогать вызовы.
 */
export function getScoreOfLabel(ageGroup: AgeGroupId | string, _lang: "ru" | "az" | "en"): string {
  return `/ ${getMaxScore(ageGroup)}`;
}

/**
 * Localized "Maximum score: N" line for the legend at the bottom of the
 * archetype-map page. Returns the full sentence.
 */
export function getMaxScoreLegend(ageGroup: AgeGroupId | string, lang: "ru" | "az" | "en"): string {
  const max = getMaxScore(ageGroup);
  if (lang === "az") return `Maksimum xal: ${max}.`;
  if (lang === "en") return `Maximum score: ${max}.`;
  return `Максимум баллов: ${max}.`;
}

/**
 * Bar-fill width helper: clamps `score / max` to [0%, 100%] so an out-of-range
 * score never overflows the bar track. The number itself still renders as-is.
 */
export function barWidthPct(score: number, ageGroup: AgeGroupId | string): string {
  const max = getMaxScore(ageGroup);
  const pct = Math.max(0, Math.min(100, (score / max) * 100));
  return `${pct}%`;
}
